import {AfterViewInit, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {NavigationEnd, Router} from "@angular/router";
import {TranslatePipe} from "@ngx-translate/core";
import {IconComponent} from "../../libs/icon/icon.component";
import {KeyValueDto} from "../../repositories/dto/key-value.dto";
import {NgClass} from "@angular/common";
import {StateService} from "../../services/state/state.service";
import {NavItem} from "./nav.item";
import {filter, map} from "rxjs/operators";
import {FirstCharPipe} from "../../libs/pipes/first-char.pipe";
import {NavService} from "../../services/nav.service";
import {Subscription} from "rxjs";
import {isDefined, nonBlank, unsubscribe} from "../../libs/utils/epic.utils";
import {AccStateEnum} from "../../services/state/acc-state.enum";
import {PermissionService} from "../../services/auth/permission.service";
import {NavOrg} from "./nav-org";
import {NavUser} from "./nav-user";
import {UrlService} from "../../services/url.service";
import {SettingsService} from "../../services/settings.service";
import {collectRoutePaths, NavTree} from "./nav.tree";
import {environment} from "../../../environments/environment";

@Component({
  selector: 'acc-sidebar-body',
  imports: [
    TranslatePipe,
    IconComponent,
    NgClass,
    FirstCharPipe
  ],
  templateUrl: './sidebar-body.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sidebar-body.component.css'
})
export class SidebarBodyComponent implements OnInit, OnDestroy, AfterViewInit {

  protected readonly navService = inject(NavService);
  private readonly router = inject(Router);
  private readonly stateService = inject(StateService);
  private readonly permissionService = inject(PermissionService);
  private readonly urlService = inject(UrlService);
  private readonly settingsService = inject(SettingsService);

  private readonly isIronBackupViewer: boolean = environment.ironBackupViewer;
  private readonly subs: Subscription[] = [];
  private sidebarMini: boolean = false;
  private currentState: AccStateEnum | undefined;

  ngOnInit(): void {
    this.subs.push(
      this.stateService.getState().subscribe((state) => this.init(state)),
      this.permissionService.accChanged().subscribe(() => this.initTree(this.currentState)),
      this.settingsService.getSettingsOnChange(SettingsService.FIRST_STEPS).subscribe(() => this.initTree(this.currentState)),
      this.router.events.pipe(
        filter((event: any) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => nonBlank(event.urlAfterRedirects) ? event.urlAfterRedirects : event.url),
        map((url: string) => this.navService.getTree().getByPath(this.getUrlWithoutParams(url), this.getParams(url))),
      ).subscribe((item: NavItem | undefined) => isDefined(item) ? this.selectByItem(item) : this.navService.getTree().deselectAll())
    );
  }

  ngAfterViewInit(): void {
    this.subs.push(this.navService.getSidebarMini()
      .pipe(filter((state) => isDefined(state)))
      .subscribe((state) => this.sidebarMini = state));
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  protected selectAndNavigateByItem(item: NavItem | undefined): void {
    this.navigate(item);
    this.selectByItem(item);
  }

  protected selectByItem(item: NavItem | undefined): void {
    this.navService.select(item);
  }

  protected selectById(id: string | undefined): void {
    this.selectByItem(this.navService.getTree().getById(id));
  }

  protected init(state: AccStateEnum): void {
    const stateChanged = state !== this.currentState;
    this.currentState = state;
    if (stateChanged) {
      this.initTree(state);
    }
  }

  protected getSidebarClass(): string {
    return this.sidebarMini ? 'acc-sidebar-mini' : 'acc-sidebar-full';
  }

  private initTree(state: AccStateEnum | undefined): void {
    if (isDefined(state)) {
      if (state === AccStateEnum.ORG) {
        const prev = this.navService.getTree()?.getSelected();
        this.navService.setTree(this.withExistingPages(NavOrg.build()));
        this.selectById(isDefined(prev) ? prev.id : undefined);
      } else {
        this.navService.setTree(this.withExistingPages(NavUser.build()));
      }
    }
  }

  // Der Viewer bringt nur einen Teil der Seiten mit (app.routes.viewer.ts); ein Eintrag ohne Seite fiele auf die Uebersicht.
  private withExistingPages(tree: NavTree): NavTree {
    if (this.isIronBackupViewer) {
      const routed: Set<string> = collectRoutePaths(this.router.config);
      return new NavTree(NavTree.retainRouted(tree.getItems(), (route) => routed.has(route.replace(/^\//, ''))));
    }
    return tree;
  }

  private getUrlWithoutParams(url: string): string {
    const urlTree = this.router.parseUrl(url);
    if (isDefined(urlTree.root.children['primary'])) {
      return urlTree.root.children['primary'].segments.map((it) => it.path).join('/');
    }
    return url;
  }

  private getParams(url: string): KeyValueDto[] {
    const params: KeyValueDto[] = [];
    const urlTree = this.router.parseUrl(url);
    if (isDefined(urlTree.root.children['primary'])) {
      urlTree.root.children['primary'].segments
        .map((it) => it.parameters)
        .filter((_p, v) => v === 0)
        .forEach((p) => Object.keys(p)
          .forEach((key) => params.push(KeyValueDto.of(key, p[key]))));
    }
    return params;
  }

  private navigate(item: NavItem | undefined): void {
    if (isDefined(item) && isDefined(item.route)) {
      let commands: any[] = [item.route];
      if (item.params.length > 0) {
        const params: any = {};
        item.params.forEach((param: KeyValueDto) => {
          params[param.key] = param.value;
        });
        commands = [item.route, params];
      }

      // see https://javascript.plainenglish.io/angular-how-you-can-reload-refresh-a-single-component-or-the-entire-application-and-reuse-the-logic-c6e975a278c3
      if (item.reloadRoute && this.router.url.startsWith('/' + item.route)) {
        this.urlService.navigateWithReload(commands).then();
      } else {
        this.urlService.navigate(commands).then();
      }
    }
  }
}
